if (!customElements.get('cart-discounts')) {
  class CartDiscounts extends HTMLElement {
    static active = null;
    static sequence = 0;

    connectedCallback() {
      this.instanceId ||= ++CartDiscounts.sequence;
      this.onClick ||= (event) => {
        const button = event.target.closest?.('.cart-discounts--button, .cart-discounts--remove');
        if (!button || !this.contains(button)) return;
        event.preventDefault();
        if (button.matches('.cart-discounts--remove')) this.removeDiscount(button);
        else this.updateDiscount();
      };
      this.onKeydown ||= (event) => {
        if (event.key !== 'Enter' || event.isComposing || !event.target.matches?.('.cart-discounts--input')) return;
        event.preventDefault();
        event.stopPropagation();
        this.updateDiscount();
      };
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown);
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
      this.observer?.disconnect();
      this.observer = new MutationObserver((records) => {
        if (records.some((record) => record.type === 'childList')) this.prepareControls();
      });
      this.observer.observe(this, { childList: true, subtree: true });
      this.prepareControls();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown);
      this.observer?.disconnect();
    }

    prepareControls() {
      const input = this.querySelector('.cart-discounts--input');
      if (input) {
        const label = this.querySelector('label');
        input.id = `drop-cart-discount-${this.instanceId}`;
        label?.setAttribute('for', input.id);
      }
      this.querySelectorAll('.cart-discounts--button, .cart-discounts--remove').forEach((button) => {
        button.type = 'button';
        if (button.matches('.cart-discounts--remove') && !button.getAttribute('aria-label')) {
          const code = button.closest('.cart-discounts--row')?.querySelector('.cart-discounts--name')?.textContent.trim();
          button.setAttribute('aria-label', `${this.text('removeDiscount', 'Remove discount')}${code ? ` ${code}` : ''}`);
        }
      });
      if (CartDiscounts.active) this.setBusy(CartDiscounts.active);
    }

    text(key, fallback) {
      return window.theme?.strings?.[key] || window.theme?.variantStrings?.[key] || fallback;
    }

    normalized(code) {
      return String(code || '').trim().toUpperCase();
    }

    surface() {
      return this.closest('#Cart-Drawer, #Cart');
    }

    destination(operation) {
      const root = operation.surfaceId ? document.getElementById(operation.surfaceId) : null;
      return (this.isConnected ? this : root?.querySelector('cart-discounts')) || root;
    }

    result(operation, message, success = false) {
      const target = this.destination(operation);
      if (operation.action === 'apply' && !success) {
        const input = target?.querySelector('.cart-discounts--input');
        if (input) input.value = operation.code;
      }
      this.message(target, message, success);
    }

    message(container, message = '', success = false) {
      if (!container?.isConnected) return;
      container.querySelector('.cart-discounts--error')?.setAttribute('hidden', '');
      let region = container.querySelector('[data-drop-discount-status]');
      if (!region && !message) return;
      if (!region) {
        region = document.createElement('div');
        region.setAttribute('data-drop-discount-status', '');
        region.className = 'form-notification';
        container.append(region);
      }
      region.textContent = message;
      region.setAttribute('role', success ? 'status' : 'alert');
      region.classList.toggle('error', !success);
      region.classList.toggle('success', success);
      region.toggleAttribute('hidden', !message);
    }

    setBusy(operation) {
      if (!operation.components.has(this)) operation.components.set(this, this.getAttribute('aria-busy'));
      this.setAttribute('aria-busy', 'true');
      this.querySelectorAll('.cart-discounts--input, .cart-discounts--button, .cart-discounts--remove').forEach((control) => {
        if (!operation.controls.has(control)) {
          operation.controls.set(control, { disabled: control.disabled, aria: control.getAttribute('aria-disabled'), loading: control.classList.contains('loading') });
        }
        control.disabled = true;
        control.setAttribute('aria-disabled', 'true');
        if (operation.action === 'apply' && control.matches('.cart-discounts--button')) control.classList.add('loading');
      });
    }

    updateDiscount() {
      if (CartDiscounts.active) return CartDiscounts.active.promise;
      const input = this.querySelector('.cart-discounts--input');
      const code = input?.value.trim();
      if (!code || code.includes(',')) {
        this.message(this, this.text('discountCodeRequired', 'Enter one discount code, then select Apply.'));
        input?.focus({ preventScroll: true });
        return Promise.resolve(false);
      }
      if (Array.from(this.querySelectorAll('.cart-discounts--name')).some((pill) => this.normalized(pill.textContent) === this.normalized(code))) {
        this.message(this, this.text('discountAlreadyApplied', 'This discount code is already applied.'), true);
        return Promise.resolve(false);
      }
      return this.start('apply', code);
    }

    removeDiscount(button) {
      if (CartDiscounts.active) return CartDiscounts.active.promise;
      const code = button.closest('.cart-discounts--row')?.querySelector('.cart-discounts--name')?.textContent.trim();
      return code ? this.start('remove', code) : Promise.resolve(false);
    }

    start(action, code) {
      const operation = { action, code, surfaceId: this.surface()?.id, controls: new Map(), components: new Map() };
      const active = document.activeElement;
      if (active && this.contains(active)) operation.focus = { surfaceId: operation.surfaceId, code: active.closest('.cart-discounts--row')?.querySelector('.cart-discounts--name')?.textContent.trim(), apply: active.matches('.cart-discounts--button') };
      CartDiscounts.active = operation;
      document.querySelectorAll('cart-discounts').forEach((element) => {
        this.message(element);
        element.setBusy(operation);
      });
      operation.promise = Promise.resolve().then(() => {
        const task = () => this.perform(operation);
        return typeof window.DROPCartUI?.enqueue === 'function' ? window.DROPCartUI.enqueue(task) : task();
      }).catch(() => {
        this.result(operation, this.text('discountUnconfirmed', 'We could not confirm the discount update. Reload your cart to check it before trying again.'));
        return false;
      }).finally(() => {
        if (CartDiscounts.active === operation) CartDiscounts.active = null;
        operation.controls.forEach((state, control) => {
          control.disabled = !!state.disabled;
          if (state.aria === null) control.removeAttribute('aria-disabled');
          else control.setAttribute('aria-disabled', state.aria);
          control.classList.toggle('loading', state.loading);
        });
        operation.components.forEach((value, component) => {
          if (value === null) component.removeAttribute('aria-busy');
          else component.setAttribute('aria-busy', value);
        });
        if (operation.focus) this.restoreFocus(operation.focus);
      });
      return operation.promise;
    }

    route(name, fallback) {
      const url = new URL(window.theme?.routes?.[name] || fallback, window.location.origin);
      if (url.origin !== window.location.origin) throw new Error('Invalid cart route');
      url.pathname = url.pathname.replace(/\/$/, '').replace(/\.js$/, '') + '.js';
      return url.pathname + url.search;
    }

    async request(url, options = {}) {
      const controller = new AbortController();
      let timer;
      try {
        return await Promise.race([
          (async () => {
            const response = await fetch(url, { ...options, credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
            const data = await response.json();
            if (!response.ok || data?.errors || data?.status) {
              const error = new Error('Cart request rejected');
              error.rejected = true;
              throw error;
            }
            return data;
          })(),
          new Promise((resolve, reject) => {
            timer = setTimeout(() => { controller.abort(); reject(new Error('Cart request timed out')); }, 20000);
          })
        ]);
      } finally {
        clearTimeout(timer);
      }
    }

    isCart(cart) {
      return !!cart && Array.isArray(cart.items) && Number.isSafeInteger(cart.item_count) && cart.item_count >= 0;
    }

    codes(cart) {
      if (!Array.isArray(cart.discount_codes) || cart.discount_codes.some((entry) => typeof entry?.code !== 'string' || !entry.code.trim() || entry.code.includes(','))) throw new Error('Discount state unavailable');
      const seen = new Set();
      return cart.discount_codes.filter((entry) => {
        const key = this.normalized(entry.code);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    }

    getSectionsToRender() {
      return window.DROPCartUI.sections();
    }

    renderSections(sections, operation, allowMissingBadge = false) {
      const updates = [];
      const open = new Map();
      const active = document.activeElement;
      const component = active?.closest('cart-discounts');
      const focusRoot = component?.surface();
      const focus = focusRoot ? { surfaceId: focusRoot.id, code: active.closest('.cart-discounts--row')?.querySelector('.cart-discounts--name')?.textContent.trim(), apply: active.matches('.cart-discounts--button') } : null;
      for (const section of this.getSectionsToRender()) {
        const root = document.getElementById(section.id);
        if (!root) continue;
        if (section.id !== 'cart-drawer-toggle') open.set(section.id, !!root.querySelector('cart-discounts')?.closest('details[open]'));
        const html = sections?.[section.section];
        if (typeof html !== 'string' || !html.trim()) {
          if (allowMissingBadge && section.id === 'cart-drawer-toggle') continue;
          return false;
        }
        const content = new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector);
        if (!content) {
          if (allowMissingBadge && section.id === 'cart-drawer-toggle') continue;
          return false;
        }
        updates.push({ destination: root.querySelector(section.selector) || root, html: content.innerHTML });
      }
      if (!updates.length) return false;
      updates.forEach(({ destination, html }) => { destination.innerHTML = html; });
      open.forEach((value, id) => {
        const details = document.getElementById(id)?.querySelector('cart-discounts')?.closest('details');
        details?.toggleAttribute('open', value);
      });
      document.querySelectorAll('cart-discounts').forEach((element) => element.prepareControls());
      if (focus && !active.isConnected) operation.focus = focus;
      return true;
    }

    restoreFocus(focus) {
      const active = document.activeElement;
      if (active && active !== document.body && active !== document.documentElement && active.isConnected && !active.disabled) return;
      const root = document.getElementById(focus.surfaceId);
      const component = root?.querySelector('cart-discounts');
      if (!component || component.closest('details') && !component.closest('details[open]')) return;
      const row = focus.code && Array.from(component.querySelectorAll('.cart-discounts--row')).find((item) => this.normalized(item.querySelector('.cart-discounts--name')?.textContent) === this.normalized(focus.code));
      const target = row?.querySelector('.cart-discounts--remove') || component.querySelector(focus.apply ? '.cart-discounts--button' : '.cart-discounts--input') || component.querySelector('button');
      target?.focus({ preventScroll: true });
    }

    async readSections(operation) {
      try {
        const sections = await window.DROPCartUI.loadSections();
        return this.renderSections(sections, operation, true);
      } catch {
        return false;
      }
    }

    publish(cart, operation) {
      try {
        const EventClass = window.Shopify?.StandardEvents?.CartDiscountUpdateEvent;
        if (!cart.token || typeof EventClass?.createCartFromAjaxResponse !== 'function' || typeof publishStandardEvent !== 'function') return;
        publishStandardEvent('CartDiscountUpdateEvent', {
          discountCodes: this.codes(cart).map(({ code, applicable }) => ({ code, applicable })),
          promise: Promise.resolve({ cart: EventClass.createCartFromAjaxResponse(cart) })
        }, document.getElementById(operation.surfaceId) || document);
      } catch {}
    }

    async perform(operation) {
      let sent = false;
      let cart;
      const fallbackRoot = window.Shopify?.routes?.root || window.theme?.routes?.root_url || '/';
      try {
        const current = await this.request(this.route('cart_url', `${fallbackRoot}cart`), { headers: { Accept: 'application/json' } });
        if (!this.isCart(current)) throw new Error('Cart state unavailable');
        const records = this.codes(current);
        if (!this.destination(operation)?.isConnected) return false;
        const match = records.find((entry) => this.normalized(entry.code) === this.normalized(operation.code));
        if (operation.action === 'apply' && match?.applicable === true) {
          await this.readSections(operation);
          this.result(operation, this.text('discountAlreadyApplied', 'This discount code is already applied.'), true);
          return false;
        }
        if (operation.action === 'remove' && !match) {
          await this.readSections(operation);
          this.result(operation, this.text('discountAlreadyRemoved', 'This discount code is no longer in your cart.'), true);
          return false;
        }
        const codes = records.filter((entry) => this.normalized(entry.code) !== this.normalized(operation.code)).map((entry) => entry.code.trim());
        if (operation.action === 'apply') codes.push(operation.code);
        const url = this.route('cart_update_url', `${fallbackRoot}cart/update`);
        sent = true;
        cart = await this.request(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: codes.join(','), sections: this.getSectionsToRender().map((section) => section.section), sections_url: window.location.pathname })
        });
        if (!this.isCart(cart)) throw new Error('Cart update unconfirmed');
        const returned = this.codes(cart);
        const code = returned.find((entry) => this.normalized(entry.code) === this.normalized(operation.code));
        let rendered = false;
        try { rendered = this.renderSections(cart.sections, operation); } catch {}
        if (!rendered) rendered = await this.readSections(operation);
        this.publish(cart, operation);
        try { document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'cart-discounts', cart } })); } catch {}
        const displayNote = rendered ? '' : ` ${this.text('discountDisplayError', 'The cart display could not refresh. Reload your cart to check its latest totals.')}`;
        const target = this.destination(operation);
        if (operation.action === 'apply' && code?.applicable === false) {
          const rejection = target?.querySelector('.cart-discounts--error-text')?.textContent.trim();
          this.result(operation, (rejection || this.text('discountCodeError', 'This discount code cannot be applied to your cart.')) + displayNote);
          return false;
        }
        if (operation.action === 'apply' && code?.applicable !== true || operation.action === 'remove' && code) {
          this.result(operation, this.text('discountUnconfirmed', 'We could not confirm the discount update. Reload your cart to check it before trying again.') + displayNote);
          return false;
        }
        this.result(operation, this.text(operation.action === 'apply' ? 'discountApplied' : 'discountRemoved', operation.action === 'apply' ? 'Discount code applied. Check your updated cart totals.' : 'Discount code removed. Check your updated cart totals.') + displayNote, rendered);
        return true;
      } catch (error) {
        if (sent) await this.readSections(operation);
        const message = !sent ? this.text('discountReadError', 'We could not check your current discounts. Reload your cart before trying again.') : error.rejected ? this.text('discountRequestError', 'The discount request was rejected. Check your cart and try again.') : this.text('discountUnconfirmed', 'We could not confirm the discount update. Reload your cart to check it before trying again.');
        this.result(operation, message);
        return false;
      }
    }
  }
  customElements.define('cart-discounts', CartDiscounts);
}
